import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { HardHat, Factory, ChevronLeft, ChevronRight, Plus, ShoppingCart, Camera, RefreshCw } from 'lucide-react';

const BASE = process.env.REACT_APP_BACKEND_URL;
const API = `${BASE}/api/boards/data`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const money = (n) => (Number(n) || 0).toLocaleString('en-CA', { style: 'currency', currency: 'CAD', maximumFractionDigits: 0 });

function usePoll(url, ms, params) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState('');
  const key = JSON.stringify(params || {});
  useEffect(() => {
    let on = true;
    const load = () => axios.get(url, { ...auth(), params }).then(r => on && (setD(r.data), setErr(''))).catch(e => on && setErr(e.response?.data?.detail || 'Unavailable'));
    load();
    const t = setInterval(load, ms);
    return () => { on = false; clearInterval(t); };
  }, [url, ms, key]);
  return [d, err];
}

const t12 = (iso) => iso ? new Date(iso).toLocaleTimeString('en-CA', { hour: 'numeric', minute: '2-digit' }) : '';

// ── Who's clocked in ──────────────────────────────────────────────────────────
export function WhosIn({ size }) {
  const [d, err] = usePoll(`${API}/whos-in`, 45000);
  if (err) return <p className="p-4 text-sm text-red-600">{err}</p>;
  if (!d) return <div className="p-4 text-sm text-slate-400 animate-pulse">Checking the floor…</div>;
  if (!d.allowed) return <p className="p-4 text-sm text-slate-500" data-testid="whosin-denied">Foreman / office roles only.</p>;
  const on = (d.crew || []).filter(c => !c.out);
  const off = (d.crew || []).filter(c => c.out);
  return (
    <div className="h-full flex flex-col text-slate-800" data-testid="whosin-widget">
      <div className="px-3 pt-3 grid grid-cols-3 gap-2">
        <Big n={d.on_now} label="On the clock" tone="text-emerald-700" />
        <Big n={d.shop_now} label="Shop" icon={Factory} />
        <Big n={d.field_now} label="Field" icon={HardHat} />
      </div>
      <ul className="flex-1 min-h-0 overflow-y-auto px-3 py-2 mt-1 space-y-0.5 no-drag">
        {on.map((c, i) => (
          <li key={i} className="flex items-center gap-2 text-sm py-1 border-b border-slate-100 last:border-0">
            <span className={`h-2 w-2 rounded-full ${c.source === 'field' ? 'bg-amber-500' : 'bg-emerald-500'}`} />
            <span className="font-semibold truncate flex-1">{c.name}</span>
            {c.wo && <span className="font-mono text-xs text-blue-700">{c.wo}</span>}
            {size.w >= 4 && c.customer && <span className="text-xs text-slate-500 truncate max-w-[110px]">{c.customer}</span>}
            <span className="text-xs text-slate-500 tabular-nums">{t12(c.in)}{c.late ? <span className="text-red-600 font-bold ml-1">late</span> : null}</span>
          </li>
        ))}
        {on.length === 0 && <li className="text-xs text-slate-400 py-2">Nobody is clocked in right now.</li>}
        {off.length > 0 && size.h >= 7 && <li className="text-[10px] uppercase tracking-wide text-slate-400 pt-2">Done for today ({off.length})</li>}
        {size.h >= 7 && off.map((c, i) => <li key={`o${i}`} className="flex items-center gap-2 text-xs text-slate-500 py-0.5"><span className="truncate flex-1">{c.name}</span><span className="tabular-nums">{c.hours}h</span></li>)}
      </ul>
    </div>
  );
}
function Big({ n, label, tone = 'text-slate-800', icon: Icon }) {
  return <div className="rounded-lg bg-slate-50 border border-slate-200 px-2 py-1.5"><div className={`text-2xl font-black leading-none ${tone}`}>{n}</div><div className="text-[10px] uppercase tracking-wide text-slate-500 flex items-center gap-1 mt-0.5">{Icon && <Icon className="h-3 w-3" />}{label}</div></div>;
}

// ── Open POs ──────────────────────────────────────────────────────────────────
export function OpenPOs({ size }) {
  const navigate = useNavigate();
  const [d, err] = usePoll(`${API}/open-pos`, 60000, { limit: Math.max(6, size.h * 2) });
  return (
    <div className="h-full flex flex-col text-slate-800" data-testid="openpos-widget">
      <div className="px-3 pt-3 flex items-center gap-2">
        <div><div className="text-2xl font-black leading-none">{d?.this_month ?? '—'}</div><div className="text-[10px] uppercase tracking-wide text-slate-500">POs this month</div></div>
        <button onClick={() => navigate('/purchase-orders?new=1')} className="no-drag ml-auto inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold" data-testid="openpos-new"><Plus className="h-3.5 w-3.5" />New PO</button>
      </div>
      {err && <p className="px-3 py-2 text-xs text-red-600">{err}</p>}
      <ul className="flex-1 min-h-0 overflow-y-auto px-2 py-2 no-drag">
        {(d?.rows || []).map(p => (
          <li key={p.po_number} onClick={() => navigate(`/purchase-orders?q=${p.po_number}`)} className="flex items-center gap-2 text-xs px-1.5 py-1.5 rounded-lg hover:bg-slate-50 cursor-pointer" data-testid={`po-row-${p.po_number}`}>
            <ShoppingCart className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
            <span className="font-mono font-bold text-slate-700">{p.po_number}</span>
            <span className="font-mono text-blue-700">{p.work_order_number}</span>
            <span className="truncate flex-1 text-slate-700">{p.company_name}{size.w >= 5 && p.description ? <span className="text-slate-400"> · {p.description}</span> : null}</span>
            <span className="font-semibold tabular-nums">{money(p.total_amount || p.amount)}</span>
          </li>
        ))}
        {d && d.rows?.length === 0 && <li className="text-xs text-slate-400 p-2">No purchase orders yet.</li>}
      </ul>
    </div>
  );
}

// ── Manpower today ────────────────────────────────────────────────────────────
export function ManpowerToday({ size }) {
  const [offset, setOffset] = useState(0);
  const [d, err] = usePoll(`${API}/manpower-today`, 120000, { offset });
  const navigate = useNavigate();
  const label = offset === 0 ? 'Today' : offset === 1 ? 'Tomorrow' : offset === -1 ? 'Yesterday' : d?.date;
  return (
    <div className="h-full flex flex-col text-slate-800" data-testid="manpower-widget">
      <div className="px-3 pt-3 flex items-center gap-2">
        <button onClick={() => setOffset(o => o - 1)} className="no-drag p-1 rounded hover:bg-slate-100" data-testid="manpower-prev"><ChevronLeft className="h-4 w-4" /></button>
        <div className="flex-1"><div className="text-sm font-bold">{label} <span className="text-slate-400 font-normal text-xs">{d?.date}</span></div><div className="text-[11px] text-slate-500">{d ? `${d.jobs.length} jobs · ${d.people} people out` : ''}</div></div>
        <button onClick={() => setOffset(o => o + 1)} className="no-drag p-1 rounded hover:bg-slate-100" data-testid="manpower-next"><ChevronRight className="h-4 w-4" /></button>
      </div>
      {err && <p className="px-3 py-2 text-xs text-red-600">{err}</p>}
      <div className="flex-1 min-h-0 overflow-y-auto px-2 py-2 space-y-1.5 no-drag">
        {(d?.jobs || []).map((j, i) => (
          <div key={i} onClick={() => navigate('/manpower')} className={`rounded-lg border px-2.5 py-1.5 cursor-pointer ${j.mine ? 'border-blue-400 bg-blue-50' : 'border-slate-200 bg-white hover:bg-slate-50'}`} data-testid={`manpower-job-${i}`}>
            <div className="flex items-center gap-2 text-xs">
              {j.work_order && <span className="font-mono font-bold text-blue-700">{j.work_order}</span>}
              <span className="font-semibold truncate flex-1">{j.customer}</span>
              {j.start && <span className="text-slate-500 tabular-nums">{j.start}</span>}
              <span className="inline-flex items-center gap-1 text-slate-600"><HardHat className="h-3 w-3" />{(j.crew || []).length + (j.foreman ? 1 : 0)}</span>
            </div>
            {size.w >= 4 && <div className="text-[11px] text-slate-500 truncate">{j.description}{j.place ? ` · ${j.place}` : ''}</div>}
            {size.h >= 7 && (j.crew?.length || j.foreman) && <div className="text-[11px] text-slate-600 truncate mt-0.5">{[j.foreman && `${j.foreman} (F)`, ...(j.crew || [])].filter(Boolean).join(', ')}</div>}
          </div>
        ))}
        {d && d.jobs.length === 0 && <p className="text-xs text-slate-400 p-2">Nothing on the manpower sheet for {label?.toLowerCase()}.</p>}
      </div>
    </div>
  );
}

// ── KPI tile ──────────────────────────────────────────────────────────────────
let kpiCache = { at: 0, p: null };
function fetchKpis() {
  if (Date.now() - kpiCache.at < 60000 && kpiCache.p) return kpiCache.p;
  kpiCache = { at: Date.now(), p: axios.get(`${API}/kpis`, auth()).then(r => r.data) };
  return kpiCache.p;
}
export function KpiTile({ config, onConfig, size }) {
  const [d, setD] = useState(null);
  const [pick, setPick] = useState(false);
  useEffect(() => { let on = true; const load = () => fetchKpis().then(x => on && setD(x)).catch(() => {}); load(); const t = setInterval(load, 60000); return () => { on = false; clearInterval(t); }; }, []);
  const metric = config?.metric || 'active_projects';
  const meta = d?.meta?.[metric];
  const v = d?.kpis?.[metric];
  const fmt = (x) => x == null ? '—' : meta?.fmt === 'money' ? money(x) : Number(x).toLocaleString();
  return (
    <div className="p-3 h-full flex flex-col justify-center text-slate-800" data-testid={`kpi-${metric}`}>
      <div className="text-[11px] uppercase tracking-wider text-slate-500 truncate">{meta?.label || metric}</div>
      <div className={`${size.h <= 3 ? 'text-3xl' : size.w >= 4 || String(fmt(v)).length <= 4 ? 'text-5xl' : 'text-3xl'} font-black leading-none mt-1 tabular-nums truncate`} data-testid="kpi-value">{fmt(v)}</div>
      {pick ? (
        <select autoFocus value={metric} onChange={e => { onConfig({ metric: e.target.value }); setPick(false); }} onBlur={() => setPick(false)} className="no-drag mt-2 text-xs border border-slate-300 rounded px-1 py-0.5" data-testid="kpi-select">
          {Object.entries(d?.meta || {}).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
        </select>
      ) : <button onClick={() => setPick(true)} className="no-drag mt-2 self-start text-[11px] text-slate-400 hover:text-blue-700" data-testid="kpi-change">change metric</button>}
    </div>
  );
}

// ── UniFi camera snapshot ─────────────────────────────────────────────────────
export function CameraWidget({ config, onConfig }) {
  const [cams, setCams] = useState(null);
  const [img, setImg] = useState('');
  const [err, setErr] = useState('');
  const every = Math.max(5, Number(config?.every) || 15);
  useEffect(() => { axios.get(`${BASE}/api/cameras/list`, auth()).then(r => setCams(r.data.cameras || [])).catch(() => setCams([])); }, []);
  useEffect(() => {
    if (!config?.camera_id) return;
    let on = true;
    const snap = () => axios.get(`${BASE}/api/cameras/${config.camera_id}/snapshot`, auth()).then(r => { if (!on) return; if (r.data.success) { setImg(r.data.data); setErr(''); } else setErr(r.data.error || 'No snapshot'); }).catch(() => on && setErr('Camera unreachable'));
    snap();
    const t = setInterval(snap, every * 1000);
    return () => { on = false; clearInterval(t); };
  }, [config?.camera_id, every]);
  if (!config?.camera_id) {
    return (
      <div className="p-3 h-full flex flex-col gap-2 no-drag" data-testid="camera-setup">
        <p className="text-xs text-slate-500 flex items-center gap-1"><Camera className="h-3.5 w-3.5" />Pick a shop camera (UniFi).</p>
        {cams === null ? <p className="text-xs text-slate-400">Loading cameras…</p> : cams.length === 0 ? <p className="text-xs text-amber-700">No cameras available — check the UniFi connection under Cameras.</p> : (
          <ul className="space-y-1 overflow-y-auto">{cams.map(c => <li key={c.id}><button onClick={() => onConfig({ camera_id: c.id, name: c.name })} className="w-full text-left text-sm px-2 py-1.5 rounded-lg border border-slate-200 hover:bg-blue-50" data-testid={`camera-pick-${c.id}`}>{c.name}</button></li>)}</ul>
        )}
      </div>
    );
  }
  return (
    <div className="h-full relative bg-black rounded-b-xl overflow-hidden" data-testid="camera-widget">
      {img ? <img src={img} alt={config.name} className="w-full h-full object-contain" /> : <div className="h-full flex items-center justify-center text-slate-400 text-xs gap-2"><RefreshCw className="h-3.5 w-3.5 animate-spin" />{err || 'Fetching snapshot…'}</div>}
      <div className="absolute bottom-1 left-2 text-[10px] text-white/80 drop-shadow">{config.name} · every {every}s</div>
      <button onClick={() => onConfig({ camera_id: '' })} className="no-drag absolute top-1 right-1 text-[10px] bg-white/90 rounded px-1.5 py-0.5 text-slate-700" data-testid="camera-change">change</button>
    </div>
  );
}
